import { PAGE_HEIGHT } from './invitationStyle';

/**
 * Stack a design's visible text boxes again inside the text space (after the AI changed sizes or added
 * lines), the same way the template layouts were made (backend scripts/templates/az_layout.py):
 * keep the top-to-bottom order, keep boxes that share a row (father / shvair) side by side, shrink
 * everything evenly only if it doesn't fit, and spread spare room evenly between the rows.
 *
 * area: template.text_area {x0, x1, y0, y1} in % of the page; without it, the boxes' current extent.
 */
export const restackText = (design, area) => {
  const visible = design.text_fields.filter((f) => !f.hidden && String(f.text || '').trim());
  if (visible.length < 2) return design;
  const sorted = [...visible].sort((a, b) => (a.y ?? 50) - (b.y ?? 50));

  const rows = [];
  for (const f of sorted) {
    const last = rows[rows.length - 1];
    if (last && Math.abs((f.y ?? 50) - last.y) < 1.2) last.items.push(f);
    else rows.push({ y: f.y ?? 50, items: [f] });
  }
  const heightOf = (f) => {
    const lines = String(f.text || '').split('\n').length;
    return (f.font_size || 24) * (f.line_spacing || 1.25) * lines;
  };
  const heights = rows.map((r) => Math.max(...r.items.map(heightOf)));

  let top = area?.y0;
  let bottom = area?.y1;
  if (!(top < bottom)) {
    top = Math.min(...sorted.map((f) => (f.y ?? 50) - (heightOf(f) / PAGE_HEIGHT) * 50));
    bottom = Math.max(...sorted.map((f) => (f.y ?? 50) + (heightOf(f) / PAGE_HEIGHT) * 50));
  }
  const avail = ((bottom - top) / 100) * PAGE_HEIGHT;
  const baseGap = 12;
  const total = heights.reduce((a, b) => a + b, 0) + baseGap * (rows.length - 1);
  const k = Math.min(1, avail / total); // shrink only when it doesn't fit
  const spare = avail - total * k;
  const gap = baseGap * k + (rows.length > 1 && spare > 0 ? Math.min(spare / (rows.length - 1), baseGap * 2) : 0);
  const used = heights.reduce((a, b) => a + b * k, 0) + gap * (rows.length - 1);

  const patch = {};
  let y = (top / 100) * PAGE_HEIGHT + Math.max(0, (avail - used) / 2);
  rows.forEach((r, i) => {
    const h = heights[i] * k;
    const yc = ((y + h / 2) / PAGE_HEIGHT) * 100;
    for (const f of r.items) {
      patch[f.id] = { y: Math.round(yc * 1000) / 1000, ...(k < 1 ? { font_size: Math.max(8, Math.round((f.font_size || 24) * k)) } : {}) };
    }
    y += h + gap;
  });
  return { ...design, text_fields: design.text_fields.map((f) => (patch[f.id] ? { ...f, ...patch[f.id] } : f)) };
};

export default restackText;
